import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Menu, X, Globe, Phone } from "lucide-react";
import { useLanguage } from "../i18n/LanguageContext";
import { PHONE_HREF } from "./CallBanner";

const LanguageToggle = () => {
  const { lang, t, toggleLanguage } = useLanguage();

  return (
    <button
      onClick={toggleLanguage}
      type="button"
      aria-label={t.nav.switchLanguage}
      title={t.nav.switchLanguage}
      className="flex items-center gap-1.5 border border-gray-300 rounded-md px-2.5 py-1.5 text-sm font-semibold text-gray-700 hover:bg-gray-50 transition-colors"
    >
      <Globe size={16} />
      {lang === "en" ? "ES" : "EN"}
    </button>
  );
};

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useLanguage();

  const isActive = (path) => {
    if (path === "") return location.pathname === "/";
    return location.pathname === `/${path}`;
  };

  const handleClick = (path) => {
    navigate(path === "" ? "/" : `/${path}`);
    setIsOpen(false);
  };

  return (
    <nav className="bg-white shadow-lg fixed top-0 left-0 w-full z-50">
      <div className="max-w-7xl mx-auto px-4">
        <div className="flex justify-between gap-3 h-16">
          <div className="flex items-center min-w-0">
            <div onClick={() => handleClick("")} className="cursor-pointer">
              <img
                src="/logo_tax_solution.png"
                alt="Tax Solutions"
                className="max-h-12 w-auto max-w-full"
              />
            </div>
          </div>

          {/* Desktop Menu */}
          <div className="hidden lg:flex flex-shrink-0 items-center space-x-4 xl:space-x-8">
            <button
              onClick={() => handleClick("")}
              className={`px-3 py-5 whitespace-nowrap ${
                isActive("")
                  ? "text-cyan-400 border-b-2 border-cyan-400"
                  : "text-gray-600 hover:text-gray-900"
              }`}
            >
              {t.nav.home}
            </button>
            <button
              onClick={() => handleClick("about")}
              className={`px-3 py-5 whitespace-nowrap ${
                isActive("about")
                  ? "text-cyan-400 border-b-2 border-cyan-400"
                  : "text-gray-600 hover:text-gray-900"
              }`}
            >
              {t.nav.about}
            </button>
            <button
              onClick={() => handleClick("services")}
              className={`px-3 py-5 whitespace-nowrap ${
                isActive("services")
                  ? "text-cyan-400 border-b-2 border-cyan-400"
                  : "text-gray-600 hover:text-gray-900"
              }`}
            >
              {t.nav.services}
            </button>
            <button
              onClick={() => handleClick("faq")}
              className={`px-3 py-5 whitespace-nowrap ${
                isActive("faq")
                  ? "text-cyan-400 border-b-2 border-cyan-400"
                  : "text-gray-600 hover:text-gray-900"
              }`}
            >
              {t.nav.faq}
            </button>
            <button
              onClick={() => handleClick("contact")}
              className={`px-3 py-5 whitespace-nowrap ${
                isActive("contact")
                  ? "text-cyan-400 border-b-2 border-cyan-400"
                  : "text-gray-600 hover:text-gray-900"
              }`}
            >
              {t.nav.contact}
            </button>
            <LanguageToggle />
            <button
              onClick={() => handleClick("contact")}
              type="button"
              className="bg-red-400 text-white px-4 py-2 rounded-lg hover:bg-red-500 transition-colors cursor-pointer whitespace-nowrap"
            >
              (407)-416-9541
            </button>
          </div>

          {/* Mobile Language, Call and Menu Buttons */}
          <div className="lg:hidden flex flex-shrink-0 items-center gap-3">
            <LanguageToggle />
            <a
              href={PHONE_HREF}
              aria-label={t.nav.call}
              className="flex items-center justify-center h-10 w-10 rounded-full bg-red-400 text-white hover:bg-red-500 transition-colors"
            >
              <Phone size={18} fill="currentColor" />
            </a>
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="text-gray-600"
            >
              {isOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {isOpen && (
        <div className="lg:hidden">
          <div className="px-2 pt-2 pb-3 space-y-1">
            <button
              onClick={() => handleClick("")}
              className={`block w-full text-left px-3 py-2 ${
                isActive("")
                  ? "text-cyan-400"
                  : "text-gray-600 hover:text-gray-900"
              }`}
            >
              {t.nav.home}
            </button>
            <button
              onClick={() => handleClick("about")}
              className={`block w-full text-left px-3 py-2 ${
                isActive("about")
                  ? "text-cyan-400"
                  : "text-gray-600 hover:text-gray-900"
              }`}
            >
              {t.nav.about}
            </button>
            <button
              onClick={() => handleClick("services")}
              className={`block w-full text-left px-3 py-2 ${
                isActive("services")
                  ? "text-cyan-400"
                  : "text-gray-600 hover:text-gray-900"
              }`}
            >
              {t.nav.services}
            </button>
            <button
              onClick={() => handleClick("faq")}
              className={`block w-full text-left px-3 py-2 ${
                isActive("faq")
                  ? "text-cyan-400"
                  : "text-gray-600 hover:text-gray-900"
              }`}
            >
              {t.nav.faq}
            </button>
            <button
              onClick={() => handleClick("contact")}
              className={`block w-full text-left px-3 py-2 ${
                isActive("contact")
                  ? "text-cyan-400"
                  : "text-gray-600 hover:text-gray-900"
              }`}
            >
              {t.nav.contact}
            </button>
            <a
              href={PHONE_HREF}
              className="block w-full text-left px-3 py-2 text-red-400 hover:text-red-500 cursor-pointer"
            >
              (407)-416-9541
            </a>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
